/**
 * Design tokens hook — provides programmatic access to CSS custom properties.
 *
 * Use this when you need theme values in JS
 * (e.g., for canvas drawing, computed styles, or conditional logic).
 *
 * For styling, prefer CSS custom properties directly in CSS/CSS Modules.
 */

/** Read a CSS custom property value as resolved on `scope` (the document root by default) */
const getCssVar = (name: string, scope?: Element): string =>
  globalThis.document
    ? getComputedStyle(scope ?? document.documentElement)
        .getPropertyValue(name)
        .trim()
    : ''

type ReadVar = (name: string) => string

/** Breakpoint values in pixels */
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
} as const

/** Max-width values (just under each breakpoint) for `max-width` media queries */
export const breakpointsDown = {
  sm: 599.95,
  md: 899.95,
  lg: 1199.95,
  xl: 1535.95,
} as const

export type Breakpoint = keyof typeof breakpoints

/** Spacing multiplier — matches MUI's 8px grid */
export const spacing = (factor = 1): string => (factor === 1 ? '8px' : `${factor * 8}px`)

export interface DesignTokens {
  palette: {
    primary: { main: string; light: string; dark: string; contrastText: string }
    secondary: { main: string; light: string; dark: string; contrastText: string }
    error: { main: string; light: string; dark: string; contrastText: string }
    warning: { main: string; light: string; dark: string; contrastText: string }
    info: { main: string; light: string; dark: string; contrastText: string }
    success: { main: string; light: string; dark: string; contrastText: string }
    text: { primary: string; secondary: string; disabled: string }
    action: {
      active: string
      hover: string
      selected: string
      disabled: string
      disabledBackground: string
      focus: string
    }
    background: { default: string; paper: string }
    divider: string
    layers: string
    grey: Record<string, string>
  }
  notes: {
    main: string
    outOfScale: string
    inScale: string
    root: string
  }
  spacing: typeof spacing
  breakpoints: typeof breakpoints
  shape: { borderRadius: string }
}

const paletteColor = (name: string, v: ReadVar) => ({
  main: v(`--tuna-palette-${name}-main`),
  light: v(`--tuna-palette-${name}-light`),
  dark: v(`--tuna-palette-${name}-dark`),
  contrastText: v(`--tuna-palette-${name}-contrastText`),
})

/**
 * Get all design tokens by reading current CSS custom property values.
 *
 * Pass `scope` to read the tokens as they resolve on that element: a subtree pinned to a theme
 * (class `dark` or `light`) redefines the tokens there, not on the document root.
 */
export const getDesignTokens = (scope?: Element): DesignTokens => {
  const v: ReadVar = name => getCssVar(name, scope)
  return {
    palette: {
      primary: paletteColor('primary', v),
      secondary: paletteColor('secondary', v),
      error: paletteColor('error', v),
      warning: paletteColor('warning', v),
      info: paletteColor('info', v),
      success: paletteColor('success', v),
      text: {
        primary: v('--tuna-palette-text-primary'),
        secondary: v('--tuna-palette-text-secondary'),
        disabled: v('--tuna-palette-text-disabled'),
      },
      action: {
        active: v('--tuna-palette-action-active'),
        hover: v('--tuna-palette-action-hover'),
        selected: v('--tuna-palette-action-selected'),
        disabled: v('--tuna-palette-action-disabled'),
        disabledBackground: v('--tuna-palette-action-disabledBackground'),
        focus: v('--tuna-palette-action-focus'),
      },
      background: {
        default: v('--tuna-palette-background-default'),
        paper: v('--tuna-palette-background-paper'),
      },
      divider: v('--tuna-palette-divider'),
      layers: v('--tuna-palette-layers'),
      grey: Object.fromEntries(
        ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map(shade => [
          shade,
          v(`--tuna-palette-grey-${shade}`),
        ])
      ),
    },
    notes: {
      main: v('--tuna-notes-main'),
      outOfScale: v('--tuna-notes-outOfScale'),
      inScale: v('--tuna-notes-inScale'),
      root: v('--tuna-notes-root'),
    },
    spacing,
    breakpoints,
    shape: {
      borderRadius: v('--tuna-shape-borderRadius'),
    },
  }
}

/**
 * Hook to access design tokens in components.
 *
 * Prefer using CSS custom properties directly in styles when possible.
 * Use this hook only when you need values in JS (canvas, SVG, calculations).
 */
export const useDesignTokens = (): DesignTokens => getDesignTokens()
